import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Drawer,
  Typography,
  IconButton,
  Box,
  Divider,
  Dialog,
  DialogContent,
  Button,
  Tooltip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import LaunchIcon from '@material-ui/icons/Launch';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import SpeedIcon from '@material-ui/icons/Speed';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import TuneIcon from '@material-ui/icons/Tune';
import GeofenceSection from './GeofenceSection';
import AreaTypeDefaultsModal from '../admin/AreaTypeDefaultsModal';
import PropertyLinesSection from './PropertyLinesSection';
import OperationalPropertyGroupSection from './OperationalPropertyGroupSection';
import RestrictedAreasSection from './RestrictedAreasSection';
import ServiceAreasSection from './ServiceAreasSection';
import RestrictedAreaDialog from '../../HMI/RestrictedAreaDialog';
import ProductionRateEditor from '../../SiteTakeoff/ProductionRateEditor';
import ProductionRateExplainer from '../../SiteTakeoff/ProductionRateExplainer';

const useStyles = makeStyles(() => ({
  drawer: {
    width: 380,
    flexShrink: 0,
  },
  paper: {
    width: 380,
    background: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 20px',
    borderBottom: '1px solid #e0e0e0',
  },
  title: {
    fontSize: 16,
    fontWeight: 500,
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: 20,
  },
  checklist: {
    padding: 14,
    borderRadius: 8,
    marginBottom: 24,
    border: '1px solid',
  },
  checklistIncomplete: {
    background: '#FFFBEB',
    borderColor: '#FCD34D',
  },
  checklistComplete: {
    background: '#ECFDF5',
    borderColor: '#A7F3D0',
  },
  checklistTitle: {
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  checkItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '3px 0',
    fontSize: 12,
    color: '#555',
  },
  section: {
    marginBottom: 28,
  },
}));

/**
 * PropertySettingsDrawer — owns the setup checklist UI and renders each section.
 * Hooks and data are passed from the parent so this component stays dumb.
 */
const PropertySettingsDrawer = ({
  open,
  onClose,
  url,
  jobsite,
  setupStatus,
  setupChecklist, // { checklist: { geofenceSet, propertyLinesVerified, takeoffComplete }, confirmItem, saving }
  // Geofence
  geofence, // { radius, saveRadius, saving }
  // Property lines
  propertyLines, // { countyLines, manualLines, usingFallback, saveManualLine, deleteManualLine, renameManualLine }
  // Cross-property measuring
  operationalGroup,
  // Restricted areas
  restrictedAreas, // { areas, saveArea, deleteArea }
  // Service areas (visual-only)
  serviceAreas, // { areas, saveArea, deleteArea, renameArea, updateAreaPaths }
  // Jobsite drawing
  startPolygonDraw,
  startPolygonEdit,
  // Production rates (per-takeoff)
  areas = [],
  rateOverrides = {},
  onUpdateRates,
}) => {
  const classes = useStyles();
  const [raDialogOpen, setRaDialogOpen] = useState(false);
  const [editingRA, setEditingRA] = useState(null);
  const [pendingRAPaths, setPendingRAPaths] = useState(null);
  const [drawingRA, setDrawingRA] = useState(false);
  const [editingRAShape, setEditingRAShape] = useState(null);
  const [drawingPL, setDrawingPL] = useState(false);
  const [editingPL, setEditingPL] = useState(null); // line being shape-edited
  const [drawingGroupArea, setDrawingGroupArea] = useState(false);
  const [drawingGroupBoundary, setDrawingGroupBoundary] = useState(false);
  const [editingGroupArea, setEditingGroupArea] = useState(null);
  const [drawingSA, setDrawingSA] = useState(false);
  const [editingSA, setEditingSA] = useState(null); // area being shape-edited
  const [ratesExpanded, setRatesExpanded] = useState(false);
  const [rateHelpOpen, setRateHelpOpen] = useState(false);
  const [serviceDefaultsOpen, setServiceDefaultsOpen] = useState(false);

  const hasJobsite = !!jobsite?._id;
  const {
    areasMapped = false,
    geofenceSet = false,
    propertyLinesVerified = false,
    takeoffComplete = false,
  } = setupStatus || {};
  const allComplete = areasMapped && geofenceSet && propertyLinesVerified && takeoffComplete;

  // Restricted area handlers
  const handleDrawRA = () => {
    if (!startPolygonDraw) return;
    setDrawingRA(true);
    onClose(); // close drawer so user can draw on the map
    startPolygonDraw(
      (paths) => {
        setDrawingRA(false);
        if (paths && paths.length >= 3) {
          setPendingRAPaths(paths);
          setEditingRA(null);
          setRaDialogOpen(true);
        }
      },
      () => setDrawingRA(false)
    );
  };

  const handleEditRA = (ra) => {
    setEditingRA(ra);
    setPendingRAPaths(null);
    setRaDialogOpen(true);
  };

  const handleEditRAShape = (ra) => {
    if (!startPolygonEdit || !ra?.paths) return;
    setEditingRAShape(ra);
    onClose();
    startPolygonEdit(
      ra.paths,
      async (updatedPaths) => {
        setEditingRAShape(null);
        if (updatedPaths && updatedPaths.length >= 3) {
          await restrictedAreas.saveArea({ paths: updatedPaths }, ra);
        }
      },
      () => setEditingRAShape(null)
    );
  };

  const handleSaveRA = async (formData) => {
    if (editingRA?._id) {
      await restrictedAreas.saveArea(formData, editingRA);
    } else if (pendingRAPaths) {
      await restrictedAreas.saveArea({ ...formData, paths: pendingRAPaths }, null);
    }
    setRaDialogOpen(false);
    setEditingRA(null);
    setPendingRAPaths(null);
  };

  // Property line drawing
  const handleDrawPL = () => {
    if (!startPolygonDraw) return;
    setDrawingPL(true);
    onClose(); // close drawer so user can draw on the map
    startPolygonDraw(
      async (paths) => {
        setDrawingPL(false);
        if (paths && paths.length >= 3) {
          await propertyLines.saveManualLine(paths);
        }
      },
      () => setDrawingPL(false)
    );
  };

  // Property line shape editing
  const handleEditPLShape = (line) => {
    if (!startPolygonEdit || !line?.paths) return;
    setEditingPL(line);
    onClose(); // close drawer so user can edit on the map
    startPolygonEdit(
      line.paths,
      async (updatedPaths) => {
        setEditingPL(null);
        if (updatedPaths && updatedPaths.length >= 3) {
          await propertyLines.updateManualLinePaths(line._id, updatedPaths);
        }
      },
      () => setEditingPL(null)
    );
  };

  const handleDrawGroupArea = () => {
    if (!startPolygonDraw || !operationalGroup?.group) return;
    setDrawingGroupArea(true);
    onClose();
    startPolygonDraw(
      async (paths) => {
        setDrawingGroupArea(false);
        if (paths && paths.length >= 3) {
          const name = window.prompt('Name this shared work area:', 'Group service area');
          if (name !== null) {
            await operationalGroup.createArea(paths, name.trim() || 'Group service area');
          }
        }
      },
      () => setDrawingGroupArea(false)
    );
  };

  const handleEditGroupAreaShape = (area) => {
    if (!startPolygonEdit || !area?.paths) return;
    setEditingGroupArea(area);
    onClose();
    startPolygonEdit(
      area.paths,
      async (updatedPaths) => {
        setEditingGroupArea(null);
        if (updatedPaths && updatedPaths.length >= 3) {
          await operationalGroup.updateAreaPaths(area._id, updatedPaths);
        }
      },
      () => setEditingGroupArea(null)
    );
  };

  const handleDrawGroupMemberBoundary = (member) => {
    if (!startPolygonDraw || !operationalGroup?.group) return;
    const jobSiteId = member?.jobSiteId?._id || member?.jobSiteId;
    if (!jobSiteId) return;
    operationalGroup.setViewMode('split');
    setDrawingGroupBoundary(true);
    onClose();
    startPolygonDraw(
      async (paths) => {
        setDrawingGroupBoundary(false);
        if (paths && paths.length >= 3) {
          await operationalGroup.saveMemberBoundaries([{
            jobSiteId,
            paths,
            name: `Group boundary – ${member.propertyName || 'Property'}`,
          }]);
        }
      },
      () => setDrawingGroupBoundary(false)
    );
  };

  // Service area drawing
  const handleDrawSA = () => {
    if (!startPolygonDraw) return;
    setDrawingSA(true);
    onClose(); // close drawer so user can draw on the map
    startPolygonDraw(
      async (paths) => {
        setDrawingSA(false);
        if (paths && paths.length >= 3) {
          await serviceAreas.saveArea(paths, 'Service Area');
        }
      },
      () => setDrawingSA(false)
    );
  };

  // Service area shape editing
  const handleEditSAShape = (area) => {
    if (!startPolygonEdit || !area?.paths) return;
    setEditingSA(area);
    onClose(); // close drawer so user can edit on the map
    startPolygonEdit(
      area.paths,
      async (updatedPaths) => {
        setEditingSA(null);
        if (updatedPaths && updatedPaths.length >= 3) {
          await serviceAreas.updateAreaPaths(area._id, updatedPaths);
        }
      },
      () => setEditingSA(null)
    );
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        className={classes.drawer}
        classes={{ paper: classes.paper }}
        variant="temporary"
      >
        <div className={classes.header} data-tour="site-takeoff-property-settings">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Typography className={classes.title}>Property settings</Typography>
            {jobsite?.aspirePropertyId && (
              <Tooltip title={`Open in Aspire (Property #${jobsite.aspirePropertyId})`}>
                <IconButton
                  size="small"
                  component="a"
                  href={`https://cloud.youraspire.com/app/properties/overview/${jobsite.aspirePropertyId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1565c0' }}
                >
                  <LaunchIcon style={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className={classes.body}>
          {/* Setup checklist */}
          <div className={`${classes.checklist} ${allComplete ? classes.checklistComplete : classes.checklistIncomplete}`}>
            <div className={classes.checklistTitle}>
              {allComplete ? (
                <>
                  <CheckCircleIcon style={{ fontSize: 16, color: '#047857' }} />
                  <span style={{ color: '#047857' }}>All set!</span>
                </>
              ) : (
                <span style={{ color: '#92400E' }}>Setup checklist</span>
              )}
            </div>
            {/* Areas mapped is derived (a fact), not a human sign-off. */}
            <div className={classes.checkItem}>
              {areasMapped
                ? <CheckCircleIcon style={{ fontSize: 16, color: '#10B981' }} />
                : <RadioButtonUncheckedIcon style={{ fontSize: 16, color: '#ccc' }} />}
              <span>Areas mapped</span>
            </div>
            {/* Geofence is confirmed via its own Confirm button below; shown read-only here. */}
            <div className={classes.checkItem}>
              {geofenceSet
                ? <CheckCircleIcon style={{ fontSize: 16, color: '#10B981' }} />
                : <RadioButtonUncheckedIcon style={{ fontSize: 16, color: '#ccc' }} />}
              <span>Geofence set</span>
            </div>
            {/* Property lines + takeoff are explicit human sign-offs, confirmable here. */}
            <ChecklistConfirmRow
              label="Property lines verified"
              item="propertyLinesVerified"
              stamp={setupChecklist?.checklist?.propertyLinesVerified}
              saving={setupChecklist?.saving === 'propertyLinesVerified'}
              disabled={!hasJobsite}
              onConfirm={setupChecklist?.confirmItem}
            />
            <ChecklistConfirmRow
              label="Takeoff complete"
              item="takeoffComplete"
              stamp={setupChecklist?.checklist?.takeoffComplete}
              saving={setupChecklist?.saving === 'takeoffComplete'}
              disabled={!hasJobsite}
              onConfirm={setupChecklist?.confirmItem}
            />
          </div>

          <Divider style={{ marginBottom: 20 }} />

          {/* Geofence */}
          <Box className={classes.section} data-tour="site-takeoff-geofence-section">
            <GeofenceSection
              radius={geofence?.radius}
              onRadiusChange={() => {}}
              onRadiusCommit={(val) => geofence?.saveRadius?.(val)}
              disabled={!hasJobsite}
              repositioning={geofence?.repositioning}
              hasCustomCenter={geofence?.hasCustomCenter}
              onStartReposition={geofence?.startRepositioning}
              onConfirmReposition={geofence?.confirmRepositioning}
              onCancelReposition={geofence?.cancelRepositioning}
              onResetCenter={geofence?.resetCenter}
              confirmed={geofence?.confirmed}
              onConfirm={geofence?.confirmGeofence}
              saving={geofence?.saving}
            />
          </Box>

          {/* Property lines */}
          <Box className={classes.section} data-tour="site-takeoff-property-lines-section">
            <PropertyLinesSection
              countyLines={propertyLines?.countyLines || []}
              manualLines={propertyLines?.manualLines || []}
              usingFallback={propertyLines?.usingFallback}
              countyVisible={propertyLines?.countyVisible}
              manualVisible={propertyLines?.manualVisible}
              onToggleCounty={() => propertyLines?.setCountyVisible(v => !v)}
              onToggleManual={() => propertyLines?.setManualVisible(v => !v)}
              onDrawCustom={handleDrawPL}
              onEditManualShape={handleEditPLShape}
              onDeleteManual={propertyLines?.deleteManualLine}
              onRenameManual={propertyLines?.renameManualLine}
              drawing={drawingPL}
              editing={!!editingPL}
              disabled={!hasJobsite}
            />
          </Box>

          {/* Operational property group */}
          <Box className={classes.section} data-tour="site-takeoff-operational-property-group-section">
            <OperationalPropertyGroupSection
              controller={operationalGroup}
              currentJobsiteId={jobsite?._id}
              onDrawArea={handleDrawGroupArea}
              onEditAreaShape={handleEditGroupAreaShape}
              onDrawMemberBoundary={handleDrawGroupMemberBoundary}
              drawing={drawingGroupArea || drawingGroupBoundary}
              editing={!!editingGroupArea}
              disabled={!hasJobsite}
            />
          </Box>

          {/* Restricted areas */}
          <Box className={classes.section} data-tour="site-takeoff-restricted-areas-section">
            <RestrictedAreasSection
              areas={restrictedAreas?.areas || []}
              onDraw={handleDrawRA}
              onEdit={handleEditRA}
              onEditShape={handleEditRAShape}
              onDelete={restrictedAreas?.deleteArea}
              drawing={drawingRA}
              editing={!!editingRAShape}
              disabled={!hasJobsite}
            />
          </Box>

          {/* Service areas */}
          <Box className={classes.section} data-tour="site-takeoff-service-areas-section">
            <ServiceAreasSection
              areas={serviceAreas?.areas || []}
              onDraw={handleDrawSA}
              onEditShape={handleEditSAShape}
              onDelete={serviceAreas?.deleteArea}
              onRename={serviceAreas?.renameArea}
              drawing={drawingSA}
              editing={!!editingSA}
              disabled={!hasJobsite}
            />
          </Box>

          {/* Production rates — opens in full-width modal */}
          <Box className={classes.section}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              style={{ padding: '4px 0' }}
            >
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <SpeedIcon style={{ fontSize: 18, color: '#666' }} />
                <Typography style={{ fontSize: 14, fontWeight: 500 }}>
                  Production rates
                </Typography>
                {Object.keys(rateOverrides || {}).length > 0 && (
                  <span style={{
                    fontSize: 10,
                    padding: '2px 6px',
                    background: '#EEF2FF',
                    color: '#4F46E5',
                    borderRadius: 4,
                    fontWeight: 600,
                  }}>
                    Custom
                  </span>
                )}
              </Box>
            </Box>
            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4, marginBottom: 8 }}>
              Override default rates, visit frequency, and modifier adjustments for this takeoff
            </Typography>
            <Box display="flex" style={{ gap: 8 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setRatesExpanded(true)}
                startIcon={<SpeedIcon style={{ fontSize: 14 }} />}
                style={{ textTransform: 'none', fontSize: 12, flex: 1 }}
              >
                Edit Rates & Scheduling
              </Button>
              <Button
                size="small"
                color="primary"
                startIcon={<HelpOutlineIcon style={{ fontSize: 14 }} />}
                onClick={() => setRateHelpOpen(true)}
                style={{ textTransform: 'none', fontSize: '0.72rem', minWidth: 0, padding: '4px 8px' }}
              >
                How it works
              </Button>
            </Box>
          </Box>

          <Divider style={{ margin: '8px 0' }} />

          {/* Service Defaults (Admin) */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{ padding: '4px 0', cursor: 'pointer' }}
            onClick={() => setServiceDefaultsOpen(true)}
          >
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <TuneIcon style={{ fontSize: 18, color: '#666' }} />
              <Typography style={{ fontSize: 14, fontWeight: 500 }}>
                Service defaults
              </Typography>
            </Box>
            <Typography variant="caption" color="textSecondary">
              Configure
            </Typography>
          </Box>
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
            Set which services auto-assign per area type
          </Typography>

          <Divider style={{ margin: '8px 0' }} />

          {/* Baseline estimating assumptions (2026-07 dial-in) — keep in sync with
              siteTakeoffConstants + laborCalculator (and the server calcHours port). */}
          <Box className={classes.section}>
            <Box display="flex" alignItems="center" style={{ gap: 8, padding: '4px 0' }}>
              <HelpOutlineIcon style={{ fontSize: 18, color: '#666' }} />
              <Typography style={{ fontSize: 14, fontWeight: 500 }}>
                Baseline assumptions
              </Typography>
            </Box>
            <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: 4 }}>
              How hours are derived — override per area or in Estimate settings:
              <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                <li>Plant density defaults from the area type (Low/Med/High); the per-area density dropdown overrides it</li>
                <li>Trimming, pruning and deadheading price only the planted fraction of each bed (5–75% of SF by density)</li>
                <li>Weed control on paved hardscape is crack/edge spot work — 5% of SF, not the full surface</li>
                <li>Paved areas are blown by default, not also swept — add Sweeping per area when contracted</li>
                <li>Debris removal is a monthly haul-off; weekly cleanup is covered by blowing + litter pickup</li>
                <li>Trimming/hedging auto-assigns only on Shrub Bed — High; pruning covers other beds</li>
                <li>Maintenance hours carry the seasonal heat factor (Jun–Aug ×1.25, Apr–May/Sep–Oct ×1.10)</li>
              </ul>
            </Typography>
          </Box>
        </div>
      </Drawer>

      {/* Area Type Service Defaults Modal */}
      <AreaTypeDefaultsModal
        open={serviceDefaultsOpen}
        onClose={() => setServiceDefaultsOpen(false)}
        url={url}
      />

      {/* Restricted Area Dialog — reuses existing HMI dialog */}
      <RestrictedAreaDialog
        open={raDialogOpen}
        onClose={() => {
          setRaDialogOpen(false);
          setEditingRA(null);
          setPendingRAPaths(null);
        }}
        onSave={handleSaveRA}
        editingArea={editingRA}
        url={url}
        clientId={jobsite?.client}
      />

      {/* Production Rates & Scheduling Modal */}
      <Dialog
        open={ratesExpanded}
        onClose={() => setRatesExpanded(false)}
        fullWidth
        maxWidth="md"
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1.5} style={{ borderBottom: '1px solid #e0e0e0' }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <SpeedIcon style={{ fontSize: 20, color: '#666' }} />
            <Typography style={{ fontSize: 16, fontWeight: 600 }}>
              Production Rates & Scheduling
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setRatesExpanded(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent style={{ padding: '16px 24px' }}>
          <ProductionRateExplainer
            areas={areas}
            rateOverrides={rateOverrides}
          />
          <Divider style={{ margin: '12px 0' }} />
          <ProductionRateEditor
            areas={areas}
            rateOverrides={rateOverrides}
            onChange={onUpdateRates}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={rateHelpOpen}
        onClose={() => setRateHelpOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1.5}>
          <Typography style={{ fontSize: 16, fontWeight: 600 }}>
            Production Rate Assumptions
          </Typography>
          <IconButton size="small" onClick={() => setRateHelpOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent dividers>
          <Typography style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.5, marginBottom: 12 }}>
            This explains how the current takeoff converts mapped areas into labor. Use it when you want the
            assumptions without opening the editor or changing any rates.
          </Typography>
          <ProductionRateExplainer
            areas={areas}
            rateOverrides={rateOverrides}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

// A checklist item the user explicitly confirms (property lines / takeoff). Shows
// the confirmed state + audit stamp and a Confirm/Undo button that calls onConfirm.
const ChecklistConfirmRow = ({ label, item, stamp, saving, disabled, onConfirm }) => {
  const confirmed = !!stamp?.confirmed;
  const handleClick = () => {
    if (!onConfirm || disabled || saving) return;
    onConfirm(item, !confirmed);
  };
  return (
    <div style={{ padding: '3px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {confirmed
          ? <CheckCircleIcon style={{ fontSize: 16, color: '#10B981' }} />
          : <RadioButtonUncheckedIcon style={{ fontSize: 16, color: '#ccc' }} />}
        <span style={{ fontSize: 12, color: '#555', flex: 1 }}>{label}</span>
        <Button
          size="small"
          onClick={handleClick}
          disabled={disabled || saving}
          style={{ minWidth: 0, fontSize: 11, textTransform: 'none', padding: '0 8px', color: confirmed ? '#92400E' : '#047857' }}
        >
          {saving ? '…' : confirmed ? 'Undo' : 'Confirm'}
        </Button>
      </div>
      {confirmed && stamp?.confirmedAt && (
        <div style={{ fontSize: 10.5, color: '#9CA3AF', marginLeft: 26 }}>
          {new Date(stamp.confirmedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          {stamp.confirmedBy ? ` · ${stamp.confirmedBy}` : ''}
        </div>
      )}
    </div>
  );
};

export default PropertySettingsDrawer;
